import classNames from 'classnames';
import { FunctionComponent, useEffect, useRef } from 'react';
import { Controller, useForm } from 'react-hook-form';

import { MessageBundle } from '@amzn/arb-tools';
import {
  Icon,
  SelectGroup,
  AsxMarkdownContent,
  PrimaryButton,
  PRIMARY_BUTTON_SIZE,
  useNavbarPositioning,
  ICON_NAME,
  PRIMARY_BUTTON_ICON_POSITION,
  PRIMARY_BUTTON_VARIANT,
} from '@amzn/sss-website-theme';

import {
  INTAKE_FORM_KEYS,
  OrganizationCommitmentsFormValues,
  QUESTION_RESPONSE,
  validateQuestionResponse,
  yearRegex,
  VALID_YES_NO_RESPONSES,
} from 'src/components/pages/intake-form/FormEnums';
import { IntakeFormQuestionInfo, FormStrings } from 'src/constants';
import { ROUTES } from 'src/enums';
import { addRequiredLabelSymbol, t } from 'src/helpers';

import styles from './OrganizationCommitmentsForm.module.scss';

export interface OrganizationCommitmentsFormProp {
  intakeFormBundle: MessageBundle;
  handleFormSubmit: (data: OrganizationCommitmentsFormValues) => void;
  backCtaLabel: string;
  ctaLabel: string;
  defaultValues?: Partial<OrganizationCommitmentsFormValues>;
  isRebrandingActive?: boolean;
}

export const OrganizationCommitmentsForm: FunctionComponent<OrganizationCommitmentsFormProp> = ({
  intakeFormBundle,
  handleFormSubmit,
  backCtaLabel,
  ctaLabel,
  defaultValues = {},
  isRebrandingActive,
}) => {
  const {
    register,
    unregister,
    handleSubmit,
    getValues,
    setValue,
    resetField,
    clearErrors,
    control,
    watch,
    formState: { errors },
  } = useForm<OrganizationCommitmentsFormValues>({
    reValidateMode: 'onSubmit',
    defaultValues,
  });
  const errorsPresentRef = useRef<HTMLDivElement>(null);
  const formHasErrors = Object.keys(errors).length > 0;

  const { mainNavbarHeight } = useNavbarPositioning();

  const commitmentsForm = 'commitmentsForm';

  const yesNoOptions = [
    { value: QUESTION_RESPONSE.YES, optionLabel: t(intakeFormBundle, FormStrings.YES_SELECTOR_OPTION) },
    { value: QUESTION_RESPONSE.NO, optionLabel: t(intakeFormBundle, FormStrings.NO_SELECTOR_OPTION) },
  ];

  useEffect(() => {
    if (formHasErrors && errorsPresentRef.current) {
      // We want to offset our scroll by our navbar height to avoid our errors present element being
      // hidden behind the navbar
      const navbarOffset = mainNavbarHeight;
      const topOffset = errorsPresentRef.current.getBoundingClientRect().top + window.scrollY - navbarOffset;
      window.scrollTo({ behavior: 'smooth', top: topOffset });
    }
  }, [formHasErrors, mainNavbarHeight]);

  const watchQuestionOneResponse = watch(INTAKE_FORM_KEYS.NET_ZERO_CARBON_TARGET_CRITERIA_QUESTION)?.includes(
    QUESTION_RESPONSE.YES
  );

  useEffect(() => {
    clearErrors();

    if (watchQuestionOneResponse) {
      register(INTAKE_FORM_KEYS.SUBQUESTION_1);
    } else if (!watchQuestionOneResponse) {
      resetField(INTAKE_FORM_KEYS.SUBQUESTION_1);
      unregister(INTAKE_FORM_KEYS.SUBQUESTION_1);
    }
  }, [clearErrors, watchQuestionOneResponse, register, unregister, resetField]);

  return (
    <div>
      {formHasErrors && (
        <div className={styles.formErrorsPresentWrapper} ref={errorsPresentRef}>
          <Icon name="error" className={styles.formErrorsPresentIcon} />
          <p className={styles.formErrorsPresent}>{t(intakeFormBundle, FormStrings.ERRORS_PRESENT_DESCRIPTION)}</p>
        </div>
      )}
      <div>
        <p className={styles.formRequiredFields}>{t(intakeFormBundle, FormStrings.REQUIRED_FIELDS_LABEL)}</p>
      </div>
      <div>
        <h4 className={styles.formSectionHeader}>{t(intakeFormBundle, FormStrings.COMMITMENTS_INFORMATION_HEADER)}</h4>
      </div>
      {/* eslint-disable-next-line @typescript-eslint/no-misused-promises */}
      <form onSubmit={handleSubmit(handleFormSubmit)} noValidate id={commitmentsForm}>
        <div className={styles.formInputWrapperLarge}>
          <Controller
            name={INTAKE_FORM_KEYS.NET_ZERO_CARBON_TARGET_CRITERIA_QUESTION}
            control={control}
            rules={{
              required: {
                value: true,
                message: t(intakeFormBundle, FormStrings.QUESTION_1_ERROR),
              },
              validate: (value) =>
                validateQuestionResponse(
                  value,
                  VALID_YES_NO_RESPONSES,
                  t(intakeFormBundle, FormStrings.QUESTION_1_ERROR)
                ),
            }}
            render={({ field: { onChange } }) => (
              <SelectGroup
                isRebrandingActive={isRebrandingActive}
                selectionType="single"
                id={INTAKE_FORM_KEYS.NET_ZERO_CARBON_TARGET_CRITERIA_QUESTION}
                label={addRequiredLabelSymbol(
                  t(intakeFormBundle, IntakeFormQuestionInfo.NET_ZERO_CARBON_TARGET_CRITERIA_QUESTION.text)
                )}
                options={yesNoOptions}
                testId={INTAKE_FORM_KEYS.NET_ZERO_CARBON_TARGET_CRITERIA_QUESTION}
                onChange={(selectedValue, isChecked) => {
                  if (isChecked) {
                    onChange(selectedValue);
                    setValue(INTAKE_FORM_KEYS.NET_ZERO_CARBON_TARGET_CRITERIA_QUESTION, selectedValue!);
                  } else {
                    onChange('');
                    setValue(INTAKE_FORM_KEYS.NET_ZERO_CARBON_TARGET_CRITERIA_QUESTION, '');
                  }
                  clearErrors(INTAKE_FORM_KEYS.NET_ZERO_CARBON_TARGET_CRITERIA_QUESTION);
                }}
                errors={errors[INTAKE_FORM_KEYS.NET_ZERO_CARBON_TARGET_CRITERIA_QUESTION]}
                showAdditionalInput={watchQuestionOneResponse}
                additionalInputProps={{
                  label: addRequiredLabelSymbol(t(intakeFormBundle, IntakeFormQuestionInfo.SUBQUESTION_1.text)),
                  id: INTAKE_FORM_KEYS.SUBQUESTION_1,
                  additionalInputValue: getValues(INTAKE_FORM_KEYS.SUBQUESTION_1) ?? '',
                  onChange: (nestedInputValue) => {
                    setValue(INTAKE_FORM_KEYS.SUBQUESTION_1, nestedInputValue);
                    clearErrors(INTAKE_FORM_KEYS.SUBQUESTION_1);
                  },
                  register,
                  registerOptions: {
                    required: t(intakeFormBundle, FormStrings.SUBQUESTION_1_ERROR),
                    pattern: {
                      value: yearRegex,
                      message: t(intakeFormBundle, FormStrings.ERROR_YEAR_DATE),
                    },
                  },
                  error: errors[INTAKE_FORM_KEYS.SUBQUESTION_1],
                  placeholder: 'YYYY',
                }}
              />
            )}
          />
        </div>
        <div className={styles.formInputWrapperLarge}>
          <Controller
            name={INTAKE_FORM_KEYS.MEASURE_AND_REPORT_CRITERIA_QUESTION}
            control={control}
            rules={{
              required: {
                value: true,
                message: t(intakeFormBundle, FormStrings.QUESTION_4_ERROR),
              },
              validate: (value) =>
                validateQuestionResponse(
                  value,
                  VALID_YES_NO_RESPONSES,
                  t(intakeFormBundle, FormStrings.QUESTION_4_ERROR)
                ),
            }}
            render={({ field: { onChange } }) => (
              <SelectGroup
                isRebrandingActive={isRebrandingActive}
                selectionType="single"
                id={INTAKE_FORM_KEYS.MEASURE_AND_REPORT_CRITERIA_QUESTION}
                label={addRequiredLabelSymbol(
                  t(intakeFormBundle, IntakeFormQuestionInfo.MEASURE_AND_REPORT_CRITERIA_QUESTION.text)
                )}
                options={yesNoOptions}
                testId={INTAKE_FORM_KEYS.MEASURE_AND_REPORT_CRITERIA_QUESTION}
                onChange={(selectedValue, isChecked) => {
                  if (isChecked) {
                    onChange(selectedValue);
                    setValue(INTAKE_FORM_KEYS.MEASURE_AND_REPORT_CRITERIA_QUESTION, selectedValue);
                  } else {
                    onChange('');
                    setValue(INTAKE_FORM_KEYS.MEASURE_AND_REPORT_CRITERIA_QUESTION, '');
                  }
                  clearErrors(INTAKE_FORM_KEYS.MEASURE_AND_REPORT_CRITERIA_QUESTION);
                }}
                errors={errors[INTAKE_FORM_KEYS.MEASURE_AND_REPORT_CRITERIA_QUESTION]}
              />
            )}
          />
        </div>
        <div className={styles.submitButtonWrapper} data-testid="submitButtonWrapper">
          <AsxMarkdownContent
            isRebrandingActive={isRebrandingActive}
            className={styles.privacyNoticeLabel}
            copy={t(intakeFormBundle, FormStrings.ORIGINAL_INTAKE_FORM_PRIVACY_NOTICE)}
          />

          <div className={styles.buttonsWrapper}>
            <PrimaryButton
              isRebrandingActive={isRebrandingActive}
              label={backCtaLabel}
              icon={ICON_NAME.CHEVRON_LEFT}
              iconPosition={PRIMARY_BUTTON_ICON_POSITION.Before}
              data-testid="back-button"
              variant={isRebrandingActive ? PRIMARY_BUTTON_VARIANT.Rate : PRIMARY_BUTTON_VARIANT.Default}
              buttonSize={PRIMARY_BUTTON_SIZE.Small}
              className={styles.backButton}
              link={ROUTES.INTAKE_PAGE_STEP_1}
              forceServerSideNav
            />
            <PrimaryButton
              isRebrandingActive={isRebrandingActive}
              label={ctaLabel}
              icon={ICON_NAME.CHEVRON_RIGHT}
              type="submit"
              form={commitmentsForm}
              data-testid="submit-button"
              buttonSize={PRIMARY_BUTTON_SIZE.Small}
              className={classNames(!isRebrandingActive && styles.submitButton)}
            />
          </div>
        </div>
      </form>
    </div>
  );
};
